import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";
import { CornerFlourish } from "./Paisley";
import { subscribeNewsletter } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      toast.success("Welcome to the house of Asvasa", {
        description: "Your first letter arrives soon.",
      });
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <footer
      className="relative bg-[#1A1A1A] text-[#c3ba9e]"
      data-testid="site-footer"
    >
      <CornerFlourish className="absolute top-0 left-0 text-[#c3ba9e]/40" />
      <CornerFlourish className="absolute top-0 right-0 text-[#c3ba9e]/40" flip />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-10">
        {/* Newsletter */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <p className="eyebrow text-[#c3ba9e]/70 shimmer-line">The Asvasa Letter</p>
          <h3 className="font-serif-display text-4xl md:text-5xl mt-6 text-[#c3ba9e]">
            Receive our seasonal dispatches
          </h3>
          <p className="text-[#c3ba9e]/65 mt-4 text-[15px]">
            Studio notes, new arrivals, and the occasional private sale — delivered once a month, never more.
          </p>
          <form
            onSubmit={submit}
            className="mt-8 flex items-center gap-0 max-w-md mx-auto border-b border-[#c3ba9e]/35 focus-within:border-[#c3ba9e] transition-colors"
          >
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent py-3 text-[15px] text-[#c3ba9e] outline-none placeholder:text-[#c3ba9e]/40"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="text-[11px] tracking-[0.28em] uppercase text-[#c3ba9e] hover:text-[#FDFAF6] px-4 py-3 transition-colors"
              data-testid="newsletter-submit"
            >
              Subscribe
            </button>
          </form>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 mb-16">
          <div>
            <p className="eyebrow text-[#c3ba9e]/70 mb-5">Shop</p>
            <ul className="space-y-3 text-[#c3ba9e]/80 text-sm">
              <li><Link to="/shop?category=shirts" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">Shirts</Link></li>
              <li><Link to="/shop?category=kurtas" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">Short Kurtas</Link></li>
              <li><Link to="/shop" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">All Pieces</Link></li>
              <li><Link to="/shop?sort=newest" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">New Arrivals</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[#c3ba9e]/70 mb-5">House</p>
            <ul className="space-y-3 text-[#c3ba9e]/80 text-sm">
              <li><Link to="/blog" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">The Journal</Link></li>
              <li><a href="mailto:asvasaclothing@gmail.com" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all">Contact</a></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[#c3ba9e]/70 mb-5">Care</p>
            <ul className="space-y-3 text-[#c3ba9e]/80 text-sm">
              <li><Link to="/shipping" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all" data-testid="footer-shipping">Shipping</Link></li>
              <li><Link to="/returns" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all" data-testid="footer-returns">Returns</Link></li>
              <li><Link to="/size-guide" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all" data-testid="footer-size-guide">Size Guide</Link></li>
              <li><Link to="/garment-care" className="hover:text-[#c3ba9e] hover:translate-x-0.5 inline-block transition-all" data-testid="footer-garment-care">Garment Care</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[#c3ba9e]/70 mb-5">Follow</p>
            <div className="flex gap-5 text-[#c3ba9e]/80">
              <a href="#" aria-label="Instagram" className="hover:text-[#c3ba9e] hover:-translate-y-0.5 transition-all"><Instagram size={18} /></a>
              <a href="#" aria-label="Facebook" className="hover:text-[#c3ba9e] hover:-translate-y-0.5 transition-all"><Facebook size={18} /></a>
              <a href="#" aria-label="Twitter" className="hover:text-[#c3ba9e] hover:-translate-y-0.5 transition-all"><Twitter size={18} /></a>
            </div>
            <p className="text-xs text-[#c3ba9e]/50 mt-6 leading-relaxed">
              Ahmedabad<br />
              Customer Service Hrs 09:00 am to 07:00 pm<br />
              Shipping Pan India
            </p>
          </div>
        </div>

        {/* Big brand wordmark */}
        <div className="border-t border-[#c3ba9e]/15 pt-10">
          <p className="font-serif-display italic text-7xl md:text-9xl text-[#c3ba9e]/10 leading-none tracking-tight select-none pointer-events-none">
            asvasa
          </p>
        </div>

        <div className="border-t border-[#c3ba9e]/15 mt-8 pt-8 flex flex-col md:flex-row gap-4 justify-between items-center text-xs text-[#c3ba9e]/55">
          <p>© {new Date().getFullYear()} Asvasa Clothing. All rights reserved.</p>
          <p className="font-serif-display italic text-sm text-[#c3ba9e]/80">
            Inspired by tradition, crafted for modern men
          </p>
        </div>
      </div>
    </footer>
  );
}
